/**
 * WHO CONSENTS to an approved change in this room (EXPERT-ROOMS §14d, owner 2026-11-04: only the intent comes from a
 * person). The expert's agent signs after its checks; consent is the room's choice — an AGENT of this room's network
 * (default: the lead) or a PERSON (the room's owner). By default a change that removes or replaces something always
 * waits for a person. Only the room's owner (or a workspace owner / admin) changes it.
 */
import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';

export default function DeployConsentSettings({ deployIntents, onSave }) {
  const c = deployIntents?.consent;
  const agents = deployIntents?.agents || [];
  const can = !!deployIntents?.canSetConsent;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  if (!c || !deployIntents?.available) return null;     // no plugin offers approved changes here → nothing
  const save = async (patch) => {
    setBusy(true); setErr('');
    try {
      await onSave?.({ action: 'setConsent', mode: c.mode, agentId: c.agentId || '',
        humanForDestructive: c.humanForDestructive !== false, ...patch });
    } catch (e) { setErr(e?.message || "Couldn't save"); }
    setBusy(false);
  };
  const chosen = c.agentId || c.effectiveAgentId || '';
  return (
    <div className="space-y-2 text-xs" data-testid="deploy-consent-settings">
      <p className="text-gray-600">The expert's agent signs a change once its checks pass. Who consents for this room?</p>
      <div className="flex gap-3" role="radiogroup" aria-label="Who consents">
        {[['agent', 'An agent'], ['human', 'A person (the room owner)']].map(([m, label]) => (
          <label key={m} className="inline-flex items-center gap-1.5">
            <input type="radio" name="deploy-consent-mode" checked={c.mode === m} disabled={!can || busy}
              data-testid={`deploy-consent-${m}`} onChange={() => save({ mode: m })} />{label}
          </label>
        ))}
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" /> : null}
      </div>
      {c.mode === 'agent' ? (
        <div className="rounded-md border border-gray-200 p-2" data-testid="deploy-consent-agents">
          <div className="mb-1 text-gray-500">The agent that consents:</div>
          {agents.map((a) => (
            <label key={a.id} className="flex items-center gap-1.5 py-0.5">
              <input type="radio" name="deploy-consent-agent" checked={chosen === a.id} disabled={!can || busy}
                data-testid={`deploy-consent-agent-${a.id}`} onChange={() => save({ agentId: a.id })} />
              {a.name}{!c.agentId && c.effectiveAgentId === a.id ? <span className="text-gray-400"> (default)</span> : null}
            </label>
          ))}
          {!agents.length ? <p className="text-amber-700">This room's network has no agents — a person consents.</p> : null}
        </div>
      ) : null}
      <label className="flex items-start gap-1.5">
        <input type="checkbox" checked={c.humanForDestructive !== false} disabled={!can || busy}
          data-testid="deploy-consent-destructive" onChange={(e) => save({ humanForDestructive: e.target.checked })} />
        <span>A change that removes or replaces something always waits for a person</span>
      </label>
      {!can ? <p className="text-gray-400">Only the room's owner changes this.</p> : null}
      {c.setByName ? <p className="text-gray-400">Set by {c.setByName}</p> : null}
      {err ? <p className="text-rose-600" data-testid="deploy-consent-error">{err}</p> : null}
    </div>
  );
}
